import type { PrecipFrequency } from '../../types';

interface Props {
  precipData: PrecipFrequency[];
}

export default function PrecipPanel({ precipData }: Props) {
  if (!precipData || precipData.length === 0) {
    return (
      <div className="panel">
        <div className="panel-title">Precipitation Frequency</div>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
          Precipitation data unavailable.
        </p>
      </div>
    );
  }

  // Group by duration
  const durations = [...new Set(precipData.map((d) => d.duration_minutes))].sort(
    (a, b) => a - b
  );
  const returnPeriods = [...new Set(precipData.map((d) => d.return_period_years))].sort(
    (a, b) => a - b
  );

  const formatDuration = (mins: number): string => {
    if (mins < 60) return `${mins}min`;
    if (mins < 1440) return `${mins / 60}hr`;
    return `${mins / 1440}day`;
  };

  const getDepth = (duration: number, period: number): string => {
    const entry = precipData.find(
      (d) => d.duration_minutes === duration && d.return_period_years === period
    );
    return entry ? entry.depth_inches.toFixed(2) : '—';
  };

  return (
    <div className="panel">
      <div className="panel-title">Precipitation Frequency (NOAA Atlas 14)</div>
      <div style={{ overflowX: 'auto' }}>
        <table className="peak-flow-table">
          <thead>
            <tr>
              <th>Duration</th>
              {returnPeriods.map((rp) => (
                <th key={rp}>{rp}-yr</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {durations.slice(0, 6).map((dur) => (
              <tr key={dur}>
                <td>{formatDuration(dur)}</td>
                {returnPeriods.map((rp) => (
                  <td key={rp}>{getDepth(dur, rp)}"</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
        Depths in inches. Source: NOAA Atlas 14 Vol 1 (Semiarid Southwest).
      </p>
    </div>
  );
}
